// Pure helpers for the onboarding wizard (docs/design/mockups/onboarding.html). No React, so they are unit tested.
import { terms as accountTerms } from '../../design/terms';

export const HISTORY_DAYS = 30;

export const STEPS = [
  { label: 'Your organization', sub: 'Name, contact, time zone' },
  { label: 'Connect Microsoft 365', sub: 'Choose how to connect' },
  { label: 'Approve access', sub: 'Read-only Microsoft Graph' },
  { label: 'Check the connection', sub: 'Six automatic checks' },
  { label: 'Organize communities', termLabel: 'Organize {communities}', sub: 'Division names and mappings' },
  { label: 'Import call history', sub: `Last ${HISTORY_DAYS} days` },
  { label: 'Finish', sub: 'Invite your team' },
];

export const INDUSTRIES = ['Community association management', 'Property management', 'Other'];

export const TIME_ZONES = [
  { value: 'America/New_York', label: 'Eastern Time (America/New_York)' },
  { value: 'America/Chicago', label: 'Central Time (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona Time (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (Pacific/Honolulu)' },
];

// estimatedMonthlyCalls is an integer in the contract; each band is stored as its upper bound.
export const CALL_VOLUMES = [
  { value: 50000, label: '10,000 to 50,000' },
  { value: 10000, label: 'Under 10,000' },
  { value: 100000, label: 'Over 50,000' },
];

export function callVolumeBand(estimate) {
  const n = Number(estimate);
  if (!n) return CALL_VOLUMES[0].value;
  if (n <= 10000) return 10000;
  if (n <= 50000) return 50000;
  return 100000;
}

/** Wizard step to show for a stored onboarding (OnboardingStep from the API). */
export function stepForStatus(status) {
  switch (status?.currentStep) {
    case 'GraphApiConfigured':
      return 3;
    case 'PermissionsValidated':
      return 4;
    case 'DataImportStarted':
    case 'DataImportCompleted':
      return 5;
    case 'OnboardingCompleted':
      return 6;
    case 'SignupReceived':
    case 'AccountCreated':
    case 'StorageProvisioned':
      return 1;
    default:
      return 0;
  }
}

export function formFromStatus(status, user) {
  const metadata = status?.metadata || {};
  return {
    companyName: status?.companyName || '',
    industry: metadata.industry || INDUSTRIES[0],
    contactName: status?.contactName || user?.name || '',
    email: status?.contactEmail || user?.email || '',
    timeZone: metadata.timeZone || TIME_ZONES[0].value,
    estimatedMonthlyCalls: callVolumeBand(metadata.estimatedMonthlyCalls),
  };
}

export function storedValidation(status) {
  const json = status?.metadata?.validation;
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

const START_ERRORS = {
  PERSONAL_ACCOUNT: "That account belongs to a different Microsoft 365 tenant",
  INVALID_TENANT: "That account belongs to a different Microsoft 365 tenant",
  TENANT_LINKED: 'This Microsoft 365 tenant is already connected',
  ONBOARDING_IN_PROGRESS: 'Someone else is already setting up Vertolytics',
};

/** Error shown on "Connect Microsoft 365" when onboarding can't start for the signed-in account. */
export function connectErrorFromStart(error) {
  const data = error?.response?.data || {};
  return {
    title: START_ERRORS[data.code] || "Setup couldn't start",
    body: data.message || 'Vertolytics could not start setup. Try again, or sign in with a different account.',
  };
}

/**
 * Error shown on "Check the connection" when the checks can't run. ADMIN_ROLE_REQUIRED: the checks passed but the
 * person who started setup isn't a Microsoft 365 administrator, so they can't become the first Vertolytics admin.
 */
export function checkErrorFromValidate(error) {
  const data = error?.response?.data || {};
  if (data.code === 'ADMIN_ROLE_REQUIRED') {
    return {
      adminRequired: true,
      title: 'Ask your Microsoft 365 admin to set this up',
      body: data.message || "Your account isn't a Microsoft 365 administrator, so it can't become the first Vertolytics administrator.",
    };
  }
  return { adminRequired: false, title: "The checks couldn't run", body: data.message || "Vertolytics couldn't reach Microsoft Graph. Try again in a minute." };
}

const CONSENT_REASONS = {
  AADSTS65004: 'the administrator declined consent',
  AADSTS90094: 'approval needs a Global Administrator or Privileged Role Administrator',
  AADSTS50020: 'the account is not in this Microsoft 365 tenant',
};

/** Reads ?consent=approved|failed&error=&error_description= from Vertolytics' consent callback redirect. */
export function consentOutcome(search) {
  const params = new URLSearchParams(search || '');
  const consent = params.get('consent');
  if (!consent) return null;
  if (consent === 'approved') return { approved: true };

  const error = params.get('error') || 'consent_failed';
  const description = params.get('error_description') || '';
  const code = (description.match(/AADSTS\d+/) || [])[0];
  let body;
  if (code) {
    body = `Microsoft returned ${code}: ${CONSENT_REASONS[code] || description.replace(/^AADSTS\d+:\s*/, '').split(/\r?\n|Trace ID/)[0].trim()}. Vertolytics can't read call records until access is approved. If you want to review the request first, send it to your IT team.`;
  } else if (error === 'access_denied') {
    body = "The administrator didn't approve access. Vertolytics can't read call records until access is approved. If you want to review the request first, send it to your IT team.";
  } else {
    body = description || "Microsoft didn't confirm the approval. Try again.";
  }
  const title = error === 'consent_not_confirmed' ? "Microsoft hasn't confirmed the approval yet" : "Access wasn't approved";
  return { approved: false, error, title, body };
}

export function checkTone(level) {
  if (level === 'Failed') return { ci: 'bad', icon: 'x', badge: 'b-critical', label: 'Failed' };
  if (level === 'Warning') return { ci: 'warn', icon: 'alert', badge: 'b-warning', label: 'Needs review' };
  return { ci: 'ok', icon: 'check', badge: 'b-good', label: 'Passed' };
}

export function checksSummary(result, companyName) {
  const checks = result?.permissions || [];
  const failed = checks.filter((c) => c.level === 'Failed');
  const warnings = checks.filter((c) => c.level === 'Warning');
  const passed = checks.length - failed.length - warnings.length;
  if (failed.length > 0) {
    const callRecords = failed.some((c) => c.dataType === 'Read call records');
    return {
      tone: 'error',
      title: failed.length === 1 ? 'One check failed' : `${failed.length} checks failed`,
      body: callRecords
        ? "Call records can't be read yet. Wait a few minutes and run the checks again, or ask your administrator to confirm consent in Entra ID under Enterprise applications › Vertolytics."
        : 'Fix the failed checks, then run the checks again. Your administrator can review the approved permissions in Entra ID under Enterprise applications › Vertolytics.',
    };
  }
  const review = warnings.length ? `, ${warnings.length} ${warnings.length === 1 ? 'needs' : 'need'} review` : '';
  return {
    tone: 'success',
    title: `Connected to ${companyName}`,
    body: `${passed} ${passed === 1 ? 'check' : 'checks'} passed${review}. You can continue.`,
  };
}

const TYPE_LABEL = { AutoAttendant: 'Auto attendant', CallQueue: 'Call queue' };

export function routingTypeLabel(type) {
  return TYPE_LABEL[type] || type;
}

export function topDivision(nodeId, nodesById) {
  let node = nodesById[nodeId];
  let guard = 0;
  while (node && node.parentId && nodesById[node.parentId] && guard++ < 20) node = nodesById[node.parentId];
  return node ? node.id : '';
}

/**
 * Rows in the mockup's order: auto attendants (organization-wide lines first), then call queues that serve a
 * Division (in Division order), then community lines, then organization-wide queues; by name within each.
 */
export function orderMappingRows(rows, nodes) {
  const divisionOrder = new Map(
    [...(nodes || [])]
      .filter((n) => !n.parentId)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.name).localeCompare(String(b.name)))
      .map((n, i) => [n.id, i])
  );
  const rank = (row) => {
    const auto = row.mapping.routingEntityType === 'AutoAttendant';
    if (auto) return [0, row.divisionId || row.communityId ? 1 : 0, 0];
    if (row.communityId) return [1, 1, 0];
    if (row.divisionId) return [1, 0, divisionOrder.get(row.divisionId) ?? Number.MAX_SAFE_INTEGER];
    return [1, 2, 0];
  };
  return [...rows].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    return ra[0] - rb[0] || ra[1] - rb[1] || ra[2] - rb[2] || String(a.mapping.routingEntityName).localeCompare(String(b.mapping.routingEntityName));
  });
}

/** The short name for copy once the organization step has run: the account's, else the organization name's first word (as the server sets it). */
export function onboardingShortName(status, accountShortName, fallback) {
  if (!status?.companyName) return fallback;
  if (accountShortName) return accountShortName;
  const first = String(status.companyName).trim().split(/\s+/)[0] || fallback;
  return first.slice(0, 40);
}

/**
 * Rows for "Suggested mappings": every mapped or discovered auto attendant and call queue with its Division and
 * community choice. A stored target wins; otherwise the A009 name-match suggestion; otherwise none.
 */
export function mappingRows(mappings, suggestions, nodes, communities) {
  const nodesById = Object.fromEntries((nodes || []).map((n) => [n.id, n]));
  const communitiesById = Object.fromEntries((communities || []).map((c) => [c.id, c]));
  const suggestionById = Object.fromEntries((suggestions || []).map((s) => [s.routingEntityId, s]));

  const rows = (mappings || [])
    .map((mapping) => {
      const suggestion = !mapping.communityId && !mapping.hierarchyNodeId ? suggestionById[mapping.routingEntityId] : null;
      const communityId = mapping.communityId || suggestion?.suggestedCommunityId || '';
      const community = communitiesById[communityId];
      const nodeId = community ? community.hierarchyNodeId : mapping.hierarchyNodeId || suggestion?.suggestedHierarchyNodeId || '';
      return {
        mapping,
        divisionId: nodeId ? topDivision(nodeId, nodesById) : '',
        communityId,
        nameMatch: Boolean(suggestion),
      };
    });
  return orderMappingRows(rows, nodes);
}

/** The mapping to save for a row: a community wins (it implies its division); else the Division; else neither. */
export function mappingForRow(row) {
  return {
    ...row.mapping,
    communityId: row.communityId || null,
    hierarchyNodeId: row.communityId ? null : row.divisionId || null,
  };
}

export function rowChanged(row) {
  const next = mappingForRow(row);
  return (next.communityId || null) !== (row.mapping.communityId || null) || (next.hierarchyNodeId || null) !== (row.mapping.hierarchyNodeId || null);
}

export function csvErrorSummary(report, fileName, terms = accountTerms()) {
  const errors = report?.errors || [];
  if (errors.length === 0) return null;
  const rowsWithErrors = new Set(errors.map((e) => e.rowNumber)).size;
  const ready = (report.created || 0) + (report.updated || 0) + (report.unchanged || 0);
  const details = errors.map((e) => `Row ${e.rowNumber}: ${e.message.replace(/\.$/, '')}.`).join(' ');
  return {
    title: `${rowsWithErrors} ${rowsWithErrors === 1 ? 'row' : 'rows'} in ${fileName} ${rowsWithErrors === 1 ? 'needs' : 'need'} fixing`,
    body: `${details} The other ${ready} ${ready === 1 ? `${terms.communityLower} is` : `${terms.communitiesLower} are`} ready to import.`,
  };
}

export function csvErrorReport(report) {
  const quote = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = ['Row,Column,Problem', ...(report?.errors || []).map((e) => [e.rowNumber, quote(e.column), quote(e.message)].join(','))];
  return `${lines.join('\r\n')}\r\n`;
}

const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const DATE_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const TIME = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

export function dateRange(from, to) {
  if (!from) return '';
  const end = to ? new Date(to) : new Date();
  return `${DATE.format(new Date(from))} to ${DATE_YEAR.format(end)}`;
}

function entity(progress, type) {
  return (progress?.entityProgress || []).find((e) => e.entityType === type);
}

function minutesLeft(span) {
  if (!span) return null;
  const match = /^(?:(\d+)\.)?(\d+):(\d+):(\d+)/.exec(span);
  if (!match) return null;
  const minutes = Number(match[1] || 0) * 1440 + Number(match[2]) * 60 + Number(match[3]) + (Number(match[4]) > 0 ? 1 : 0);
  return Math.max(1, minutes);
}

export function isThrottled(progress) {
  return Boolean(progress?.isRetrying && (progress.warnings || []).some((w) => w.warningCode === 'GRAPH_THROTTLED'));
}

// The server treats an import with no progress for this long as interrupted (contracts §21); a running import
// refreshes its progress at least every minute.
export const IMPORT_STALL_MINUTES = 10;

/** 'failed' (stopped at a stage it can resume from), 'stalled' (no progress for IMPORT_STALL_MINUTES) or null. */
export function importResumeState(progress, now = Date.now()) {
  if (!progress) return null;
  if (progress.stage === 'Failed') return progress.failedStage ? 'failed' : null;
  if (progress.stage === 'Completed' || !progress.updatedAt) return null;
  return now - new Date(progress.updatedAt).getTime() >= IMPORT_STALL_MINUTES * 60 * 1000 ? 'stalled' : null;
}

/** Message for a refused resume (409 IMPORT_RUNNING / IMPORT_NOT_RESUMABLE) or any other failure. */
export function resumeErrorFrom(err) {
  const code = err?.response?.data?.code;
  if (code === 'IMPORT_RUNNING') return { title: 'The import is still running', body: 'Vertolytics received progress from it recently. Wait a few minutes; if nothing changes, resume it again.' };
  if (code === 'IMPORT_NOT_RESUMABLE') return { title: "This import can't be resumed", body: "It was cancelled, so it can't continue from where it stopped." };
  return { title: "The import couldn't be resumed", body: err?.response?.data?.message || 'Vertolytics did not respond. Try again in a minute.' };
}

/** Rows, overall percentage and time left for "Import call history". */
export function importView(progress, now = Date.now()) {
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const calls = entity(progress, 'CallRecords');
  const legs = entity(progress, 'CallLegs');
  const transcription = progress?.transcriptionBackfill || {};
  const throttled = isThrottled(progress);
  const transcriptionPct = transcription.requested ? Math.round((transcription.completed / transcription.requested) * 100) : progress?.stage === 'Completed' ? 100 : 0;
  const from = progress?.importFromDate ? new Date(progress.importFromDate) : null;
  const to = progress?.importToDate ? new Date(progress.importToDate) : new Date();

  const rows = [
    { title: 'Call records', caption: dateRange(from, to), pct: calls?.progressPercentage || 0, value: `${fmt(calls?.recordsImported)} calls` },
    { title: 'Call routes and sessions', caption: 'Auto attendant, queue, agent, transfer legs', pct: legs?.progressPercentage || 0, value: `${fmt(legs?.recordsImported)} legs` },
    {
      title: 'Transcription and analysis',
      caption: 'Calls with recordings, newest first',
      pct: transcriptionPct,
      value: throttled ? 'Paused' : transcriptionPct >= 100 ? `${fmt(transcription.completed)} calls` : `${fmt(transcription.completed)} of ${fmt(transcription.requested)}`,
      paused: throttled,
    },
  ];

  return {
    rows,
    overall: progress?.overallProgressPercentage || 0,
    done: progress?.stage === 'Completed',
    failed: progress?.stage === 'Failed',
    resume: importResumeState(progress, now),
    minutesLeft: minutesLeft(progress?.estimatedTimeRemaining),
    throttled,
  };
}

/** Activity lines, newest first: warnings, completed entities and the current operation. */
export function activityLines(progress) {
  const lines = [];
  const time = (value) => TIME.format(new Date(value));
  const noun = { CallRecords: 'call records', CallLegs: 'call legs', CallSessions: 'call sessions', Agents: 'agents', Queues: 'call queues', Phones: 'phone numbers', Presence: 'presence records' };
  (progress?.warnings || []).forEach((w) => lines.push({ at: w.occurredAt, text: `${time(w.occurredAt)} ${w.message}`, warning: true }));
  (progress?.errors || []).forEach((e) => lines.push({ at: e.occurredAt, text: `${time(e.occurredAt)} ${e.userFriendlyMessage || e.message}`, warning: true }));
  (progress?.entityProgress || [])
    .filter((e) => e.completedAt)
    .forEach((e) => lines.push({ at: e.completedAt, text: `${time(e.completedAt)} Imported ${Number(e.recordsImported || 0).toLocaleString('en-US')} ${noun[e.entityType] || e.entityType}` }));
  lines.sort((a, b) => new Date(b.at) - new Date(a.at));
  if (progress?.currentOperation && progress?.updatedAt && progress.stage !== 'Completed')
    lines.unshift({ at: progress.updatedAt, text: `${time(progress.updatedAt)} ${progress.currentOperation}` });
  return lines;
}

export function parseInvites(text) {
  return Array.from(new Set((text || '').split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter((s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s))));
}

export function possessive(name) {
  if (!name) return "your organization's";
  return name.endsWith('s') ? `${name}'` : `${name}'s`;
}

export function upnDomain(upn) {
  const at = (upn || '').lastIndexOf('@');
  return at > 0 ? upn.slice(at + 1) : '';
}
